import React, { memo, ReactElement, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { isNewWebSearchUIEnabled } from 'reactApp/appHelpers/featuresHelpers';
import { useFallbackSearchUrl } from 'reactApp/hooks/useFallbackSearchUrl';
import { historyPush } from 'reactApp/modules/router/router.module';
import { resetSelect } from 'reactApp/modules/selections/selections.actions';
import { getIdByStorage, getStorage } from 'reactApp/modules/storage/storage.helpers';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { Logo } from 'reactApp/ui/Header/Logo/Logo';
import { ToolbarItem } from 'reactApp/ui/Toolbar/ToolbarItem/ToolbarItem';
import { ChevronLeftOutlineIcon } from 'reactApp/ui/VKUIIcons';
import opener from 'reactApp/utils/opener';

import styles from './Header.css';
import { useShowBackItem } from './Header.hooks';

interface Props {
    isSearchExpanded: boolean;
    toggleSearch: (state: boolean) => void;
    storage: EStorageType;
}

export const LeftSide = memo(({ isSearchExpanded, toggleSearch, storage }: Props): ReactElement => {
    const dispatch = useDispatch();

    const { isAttaches, isReactPage } = getStorage(storage);
    const showBackItem = useShowBackItem();
    const cancelSearchFallbackPath = useFallbackSearchUrl(getIdByStorage(EStorageType.home));

    const cancelSearch = useCallback(() => {
        if (isNewWebSearchUIEnabled && isReactPage && !isAttaches) {
            const { search, pathname } = cancelSearchFallbackPath.current;

            dispatch(historyPush({ id: pathname, search }));
            toggleSearch(false);
        } else if (isAttaches) {
            dispatch(historyPush({ id: getIdByStorage(EStorageType.attaches) }));
            toggleSearch(false);
        } else if (isReactPage) {
            dispatch(historyPush({ id: getIdByStorage(EStorageType.home) }));
            toggleSearch(false);
        } else {
            // TODO: поправить после https://jira.vk.team/browse/CLOUDWEB-13172
            opener(getIdByStorage(EStorageType.home), true);
        }
    }, [dispatch, isAttaches, isReactPage, toggleSearch]);

    const resetSelection = useCallback(() => {
        dispatch(resetSelect());
    }, [dispatch]);

    const content = useMemo(() => {
        if (showBackItem) {
            return <ToolbarItem id="back" text="Вернуться" icon={<ChevronLeftOutlineIcon />} onClick={resetSelection} />;
        }

        if (isSearchExpanded) {
            return <ToolbarItem id="closeSearch" text="Сбросить поиск" icon={<ChevronLeftOutlineIcon />} onClick={cancelSearch} />;
        }

        return <Logo />;
    }, [cancelSearch, isSearchExpanded, showBackItem, resetSelection]);

    return <div className={styles.leftSide}>{content}</div>;
});

LeftSide.displayName = 'LeftSide';
